import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { AppLoadingScreen } from '@/components/AppLoadingScreen'
import { Landing } from '@/pages/Landing'
import { Login } from '@/pages/Login'
import { Signup } from '@/pages/Signup'
import { ThankYou } from '@/pages/ThankYou'
import { Download } from '@/pages/Download'
import { AppAuth } from '@/pages/AppAuth'
import { ShowDetail } from '@/pages/ShowDetail'
import { Originals } from '@/pages/Originals'
import { Creators } from '@/pages/Creators'
import { Press } from '@/pages/Press'
import { Billing } from '@/pages/Billing'
import { Advertise } from '@/pages/Advertise'
import { Settings } from '@/pages/Settings'
import { Terms } from '@/pages/Terms'
import { Privacy } from '@/pages/Privacy'
import { AdminLayout } from '@/pages/admin/AdminLayout'
import { AdminDashboard } from '@/pages/admin/Dashboard'
import { AdminContent } from '@/pages/admin/Content'
import { AdminShows } from '@/pages/admin/Shows'
import { AdminShowDetail } from '@/pages/admin/ShowDetail'
import { AdminUsers } from '@/pages/admin/Users'
import { AdminUserDetail } from '@/pages/admin/UserDetail'
import { AdminSupport } from '@/pages/admin/Support'
import { AdminAnalytics } from '@/pages/admin/Analytics'
import { SignupsAnalytics } from '@/pages/admin/SignupsAnalytics'
import RoadMap from '@/pages/RoadMap'
import CreatorAgreement from '@/pages/CreatorAgreement'
import { CreatorLayout } from '@/pages/creator/CreatorLayout'
import { CreatorDashboard } from '@/pages/creator/Dashboard'
import { CreatorSeries } from '@/pages/creator/Series'
import { CreatorSeriesDetail } from '@/pages/creator/SeriesDetail'
import { CreatorEpisodes } from '@/pages/creator/Episodes'
import { CreatorUpload } from '@/pages/creator/Upload'
import { CreatorSettings } from '@/pages/creator/Settings'
import { AdvertiserLayout } from '@/pages/advertiser/AdvertiserLayout'
import { AdvertiserDashboard } from '@/pages/advertiser/Dashboard'
import { AdvertiserSponsors } from '@/pages/advertiser/Sponsors'
import { AdvertiserAds } from '@/pages/advertiser/Ads'
import { AdvertiserPlacements } from '@/pages/advertiser/Placements'
import { AdvertiserAnalytics } from '@/pages/advertiser/Analytics'
import { AdvertiserDeals } from '@/pages/advertiser/Deals'
// App routes are disabled in preregister mode; keep imports here commented
// to avoid bundling unused app shell until launch.
// import { AppLayout } from '@/src/pages/app/AppLayout'
// import { AppHome } from '@/src/pages/app/AppHome'
// import { ForYou } from '@/src/pages/app/ForYou'
// import { MyList } from '@/src/pages/app/MyList'
// import { Gifts } from '@/src/pages/app/Gifts'
// import { Profile } from '@/src/pages/app/Profile'
// import { Creator } from '@/src/pages/app/Creator'
// import { VideoDetail } from '@/src/pages/app/VideoDetail'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    )
  }
  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />
  }
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    )
  }

  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />
  }

  if (!user.isAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function CreatorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    )
  }

  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />
  }

  // If your backend exposes a creator flag/role, enforce it here.
  // For now, allow all authenticated users while we validate the studio UX.
  return <>{children}</>
}

/**
 * AdvertiserRoute — currently gated to admin users only, since advertiser
 * accounts are not yet a separate role in the backend. Once `isAdvertiser`
 * is exposed on the User model, replace the `user.isAdmin` check below.
 */
function AdvertiserRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    )
  }

  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />
  }

  // TODO: replace with `user.isAdvertiser` once the backend exposes that flag.
  if (!user.isAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default function App() {
  const { loading } = useAuth()

  if (loading) {
    return <AppLoadingScreen />
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/preregister" element={<Navigate to="/signup" replace />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/download" element={<Download />} />
      <Route path="/app/signin" element={<AppAuth />} />
      <Route path="/app/signup" element={<AppAuth />} />
      <Route path="/shows/:filmId" element={<ShowDetail />} />
      <Route path="/originals" element={<Originals />} />
      <Route path="/creators" element={<Creators />} />
      <Route path="/press" element={<Press />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/advertise" element={<Advertise />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Admin portal */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="content/:seriesId" element={<AdminShowDetail />} />
        <Route path="shows" element={<AdminShows />} />
        <Route path="shows/:seriesId" element={<AdminShowDetail />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:userId" element={<AdminUserDetail />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="analytics/signups" element={<SignupsAnalytics />} />
        <Route path="roadmap" element={<RoadMap />} />
      </Route>

      {/* Creator studio */}
      <Route
        path="/creator"
        element={
          <CreatorRoute>
            <CreatorLayout />
          </CreatorRoute>
        }
      >
        <Route index element={<CreatorDashboard />} />
        <Route path="series" element={<CreatorSeries />} />
        <Route path="series/:id" element={<CreatorSeriesDetail />} />
        <Route path="episodes" element={<CreatorEpisodes />} />
        <Route path="upload" element={<CreatorUpload />} />
        <Route path="settings" element={<CreatorSettings />} />
        <Route path="agreement" element={<CreatorAgreement />} />
      </Route>

      {/* Advertiser portal */}
      <Route
        path="/advertiser"
        element={
          <AdvertiserRoute>
            <AdvertiserLayout />
          </AdvertiserRoute>
        }
      >
        <Route index element={<AdvertiserDashboard />} />
        <Route path="sponsors" element={<AdvertiserSponsors />} />
        <Route path="ads" element={<AdvertiserAds />} />
        <Route path="placements" element={<AdvertiserPlacements />} />
        <Route path="deals" element={<AdvertiserDeals />} />
        <Route path="analytics" element={<AdvertiserAnalytics />} />
      </Route>

      {/* /app routes are disabled while we are in preregister mode */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
